import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reportesAPI } from '../api/client';
import { useConfig } from '../context/ConfigContext';
import { 
  TrendingUp, DollarSign, ShoppingCart, Package, 
  Users, AlertTriangle, Calendar, Settings
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatCurrency, config } = useConfig();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await reportesAPI.dashboard();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Ventas Hoy',
      value: formatCurrency(stats?.ventas_hoy || 0),
      icon: DollarSign,
      color: 'bg-green-500',
      change: stats?.cambio_ventas || 0
    },
    {
      title: 'Ventas del Mes',
      value: formatCurrency(stats?.ventas_mes || 0),
      icon: TrendingUp,
      color: 'bg-blue-500',
    },
    {
      title: 'Valor Inventario',
      value: formatCurrency(stats?.valor_inventario || 0),
      icon: Package,
      color: 'bg-orange-500',
      subtitle: 'Precio de compra'
    },
    {
      title: 'Productos',
      value: stats?.total_productos || 0,
      icon: Package,
      color: 'bg-purple-500',
    },
    {
      title: 'Clientes',
      value: stats?.total_clientes || 0,
      icon: Users,
      color: 'bg-indigo-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header con Logo y Nombre Personalizado */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg shadow-lg p-6 text-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            {config?.logo ? (
              <img 
                src={config.logo} 
                alt="Logo" 
                className="h-16 w-16 object-contain bg-white rounded-lg p-2"
              />
            ) : (
              <div className="h-16 w-16 bg-white rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-primary-600" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold">
                {config?.nombre_negocio || 'ELEGANCIA'}
              </h1>
              <p className="text-primary-100 mt-1">
                {config?.slogan || 'ELEGANCIA'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 text-sm text-primary-100 mb-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <Link 
              to="/configuracion" 
              className="inline-flex items-center gap-2 text-sm hover:text-white transition-colors"
            >
              <Settings className="w-4 h-4" />
              Configurar negocio
            </Link>
          </div>
        </div>
      </div>

      {/* Tarjetas de estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className={`${stat.color} rounded-lg p-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              {stat.change !== undefined && (
                <span className={`text-sm font-medium ${stat.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stat.change >= 0 ? '+' : ''}{stat.change}%
                </span>
              )}
            </div>
            <h3 className="text-xl font-bold text-gray-900">{stat.value}</h3>
            <p className="text-sm text-gray-600 mt-1">{stat.title}</p>
            {stat.subtitle && (
              <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
            )}
          </div>
        ))}
      </div>

      {/* Gráficas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ventas de la semana */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Ventas de la Semana</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.ventas_semana || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" fill="#000000" name="Ventas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Productos más vendidos */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Productos Más Vendidos</h3>
          <div className="space-y-3">
            {(stats?.productos_top || []).slice(0, 5).map((producto, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{producto.nombre}</p>
                  <p className="text-sm text-gray-600">{producto.cantidad_vendida} unidades</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-primary-600">{formatCurrency(producto.total_vendido)}</p>
                </div>
              </div>
            ))}
            {(!stats?.productos_top || stats.productos_top.length === 0) && (
              <p className="text-gray-500 text-center py-8">No hay datos disponibles</p>
            )}
          </div>
        </div>
      </div>

      {/* Alertas y recordatorios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock bajo */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <h3 className="text-lg font-semibold">Productos con Stock Bajo</h3>
          </div>
          <div className="space-y-2">
            {(stats?.stock_bajo || []).slice(0, 5).map((producto, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span className="text-sm font-medium">{producto.nombre}</span>
                <span className="text-sm text-orange-600 font-semibold">
                  Stock: {producto.stock} / Min: {producto.stock_minimo}
                </span>
              </div>
            ))}
            {(!stats?.stock_bajo || stats.stock_bajo.length === 0) && (
              <p className="text-gray-500 text-center py-4">No hay alertas de stock</p>
            )}
          </div>
        </div>

        {/* Cuentas por cobrar */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold">Cuentas por Cobrar Vencidas</h3>
          </div>
          <div className="space-y-2">
            {(stats?.cuentas_vencidas || []).slice(0, 5).map((cuenta, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">{cuenta.cliente_nombre}</p>
                  <p className="text-xs text-gray-600">{cuenta.dias_vencido} días de retraso</p>
                </div>
                <span className="text-sm text-red-600 font-semibold">
                  {formatCurrency(cuenta.saldo)}
                </span>
              </div>
            ))}
            {(!stats?.cuentas_vencidas || stats.cuentas_vencidas.length === 0) && (
              <p className="text-gray-500 text-center py-4">No hay cuentas vencidas</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
